import { useState } from 'react';
import { TextField, Box, Button, Typography, Stack, useTheme, styled } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import { emailRegex } from 'constants/regex';
import { useTranslation } from 'react-i18next';
import { use } from 'i18next';
import { UploadButton } from './EmailsInput.styles';

const StyledUploadStack = styled(Stack)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  width: '100%',
  justifyContent: 'space-between',
  paddingTop: theme.spacing(3)
}));

const StyledHiddenInput = styled('input')({
  display: 'none'
});

interface EmailsInputProps {
  onChange: (emails: string[], invalidEmailsExist: boolean) => void;
}

export const EmailsInput: React.FC<EmailsInputProps> = ({ onChange }) => {
  const [emails, setEmails] = useState<string[]>([]);
  const [invalidEmails, setInvalidEmails] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState<string>('');
  const { t } = useTranslation();
  const theme = useTheme();
  
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const contents = event.target?.result as string;
        const newEmails = contents.split(',').map(email => email.trim());
        const invalidEmails = newEmails.filter(email => !emailRegex.test(email));
        setInvalidEmails(invalidEmails);
        setEmails(newEmails);
        onChange(newEmails, invalidEmails.length > 0);
      };
      reader.readAsText(file);
    }
  };

  const handleAutocompleteChange = (event: React.ChangeEvent<{}>, newValue: string[]) => {
    const invalidEmails = newValue.filter(email => !emailRegex.test(email));
    setEmails(newValue);
    setInvalidEmails(invalidEmails);
    onChange(newValue, invalidEmails.length > 0);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if(event.key === 'Enter' || event.key === ' ') {
      const trimmedInputValue = inputValue.trim();
      if (!trimmedInputValue) return;
      const newEmails = [...emails, trimmedInputValue];
      const invalidEmails = newEmails.filter(email => !emailRegex.test(email));
      setEmails(newEmails);
      setInvalidEmails(invalidEmails);
      onChange(newEmails, invalidEmails.length > 0);
      setInputValue('');
    }  
  }
  
  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const trimmedInputValue = inputValue.trim();
    if (!trimmedInputValue) return;
    const newEmails = [...emails, trimmedInputValue];
    const invalidEmails = newEmails.filter(email => !emailRegex.test(email));
    setEmails(newEmails);
    setInvalidEmails(invalidEmails);
    onChange(newEmails, invalidEmails.length > 0);
    setInputValue('');
  };
  
  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const pastedText = event.clipboardData.getData('text');
    const pastedEmails = pastedText.split(/[\s;,]+/).map(email => email.trim()).filter(email => email !== '');
    const newEmails = [...emails, ...pastedEmails];
    const invalidEmails = newEmails.filter(email => !emailRegex.test(email));
    setEmails(newEmails);
    setInvalidEmails(invalidEmails);
    onChange(newEmails, invalidEmails.length > 0);
  };
  
  return (
    <Box>
      <Autocomplete
        multiple
        freeSolo
        inputValue={inputValue}
        value={emails}
        onInputChange={(event, newInputValue) => {
          setInputValue(newInputValue);
        }}
        onChange={handleAutocompleteChange}
        onBlur={handleBlur}
        options={[]}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            label={t('inviteUserDialog.emailAdresses')}
            placeholder={t('inviteUserDialog.addEmail')}
            onPaste={handlePaste}
          />
        )}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              variant="outlined"
              label={option}
              {...getTagProps({ index })}
            />
          ))
        }
        onKeyDown={handleKeyDown}
      />
      {invalidEmails.length > 0 && <Typography variant="caption" color="error">
        {`${t('inviteUserDialog.invalidEmails')}: ${invalidEmails.join(', ')}`}
      </Typography>}
      <StyledUploadStack>
        <Typography variant='subtitle2' fontWeight={'400'}>{t('inviteUserDialog.orUploadFile')}</Typography>
        <StyledHiddenInput
          accept=".csv"
          id="email-file-input"
          type="file"
          onChange={handleFileChange}
        />
        <label htmlFor="email-file-input">
          <UploadButton variant="outlined" component="span">
            {t('inviteUserDialog.uploadFileButton')}
          </UploadButton>
        </label>        
      </StyledUploadStack>
    </Box>
  );
};